import {
  useCallback,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'

import { VStack, HStack, Text, Select, Spinner } from '@chakra-ui/react'

import { type MediaDeviceInfo } from '../../hooks/useMediaDevices'

interface Props {
  cameraDevices: MediaDeviceInfo[]
  microphoneDevices: MediaDeviceInfo[]
  isLoading: boolean
  currentCameraDeviceId: string | null
  currentMicrophoneDeviceId: string | null
  onApply: (
    selectedCameraId: string,
    selectedMicrophoneId: string,
  ) => Promise<void>
}

export interface DeviceSettingsRef {
  handleApply: () => Promise<void>
}

export const DeviceSettingsContent = forwardRef<DeviceSettingsRef, Props>(
  (
    {
      cameraDevices,
      microphoneDevices,
      isLoading,
      currentCameraDeviceId,
      currentMicrophoneDeviceId,
      onApply,
    },
    ref,
  ) => {
    const [selectedCameraId, setSelectedCameraId] = useState<string>('')
    const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>('')

    // 現在のデバイスIDを初期値として設定
    useEffect(() => {
      setSelectedCameraId(currentCameraDeviceId || '')
      setSelectedMicrophoneId(currentMicrophoneDeviceId || '')
    }, [currentCameraDeviceId, currentMicrophoneDeviceId])

    const handleApply = useCallback(async () => {
      await onApply(selectedCameraId, selectedMicrophoneId)
    }, [selectedCameraId, selectedMicrophoneId, onApply])

    useImperativeHandle(
      ref,
      () => ({
        handleApply,
      }),
      [handleApply],
    )

    const renderDeviceSelect = (
      label: string,
      devices: MediaDeviceInfo[],
      selectedId: string,
      onSelect: (id: string) => void,
    ) => (
      <VStack align="stretch" spacing={2}>
        <Text fontWeight="medium" color="gray.700">
          {label}
        </Text>
        <Select
          value={selectedId}
          onChange={e => onSelect(e.target.value)}
          placeholder={
            devices.length === 0
              ? '利用可能なデバイスがありません'
              : '選択してください'
          }
          disabled={devices.length === 0}
        >
          {devices.map(device => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label}
            </option>
          ))}
        </Select>
      </VStack>
    )

    if (isLoading) {
      return (
        <HStack justify="center" py={4}>
          <Spinner />
          <Text>デバイスを取得中...</Text>
        </HStack>
      )
    }

    return (
      <VStack spacing={4} align="stretch">
        {renderDeviceSelect(
          'マイク',
          microphoneDevices,
          selectedMicrophoneId,
          setSelectedMicrophoneId,
        )}

        {renderDeviceSelect(
          'カメラ',
          cameraDevices,
          selectedCameraId,
          setSelectedCameraId,
        )}
      </VStack>
    )
  },
)

DeviceSettingsContent.displayName = 'DeviceSettingsContent'
